import { useState, useEffect } from 'react';
import { keyDatesApi, DATE_TYPES } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

export const KeyDateModal = ({ open, onClose, propertyId, keyDate, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date_type: '',
    due_date: '',
    notes: '',
  });

  useEffect(() => {
    if (keyDate) {
      setFormData({
        date_type: keyDate.date_type || '',
        due_date: keyDate.due_date || '',
        notes: keyDate.notes || '',
      });
    } else {
      setFormData({
        date_type: '',
        due_date: '',
        notes: '',
      });
    }
  }, [keyDate, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date_type) {
      toast.error('Please select a date type');
      return;
    }

    if (!formData.due_date) {
      toast.error('Please select a due date');
      return;
    }

    setLoading(true);
    try {
      const data = {
        date_type: formData.date_type,
        due_date: formData.due_date,
        notes: formData.notes.trim(),
      };

      if (keyDate) {
        await keyDatesApi.update(keyDate.id, data);
        toast.success('Key date updated successfully');
      } else {
        await keyDatesApi.create({
          ...data,
          property_id: propertyId,
        });
        toast.success('Key date added successfully');
      }
      onSuccess();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save key date';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {keyDate ? 'Edit Key Date' : 'Add Key Date'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Date Type */}
          <div>
            <Label htmlFor="dateType">Date Type *</Label>
            <Select
              value={formData.date_type}
              onValueChange={(value) => setFormData({ ...formData, date_type: value })}
              disabled={loading}
            >
              <SelectTrigger className="mt-1.5" data-testid="key-date-type-select">
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                {DATE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Due Date */}
          <div>
            <Label htmlFor="dueDate">Due Date *</Label>
            <Input
              id="dueDate"
              type="date"
              data-testid="key-date-due-input"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="mt-1.5"
              disabled={loading}
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              data-testid="key-date-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any notes about this date..."
              className="mt-1.5 resize-none"
              rows={2}
              disabled={loading}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="key-date-submit-btn"
              className="bg-slate-900 hover:bg-slate-800"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner"></span>
                  Saving...
                </span>
              ) : (
                keyDate ? 'Save Changes' : 'Add Key Date'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default KeyDateModal;
